import { useState } from "react";
import { useRoute, Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import ChallengeProgressRing from "@/components/mnf/ChallengeProgressRing";
import FoundingMemberRibbon from "@/components/mnf/FoundingMemberRibbon";
import { useToast } from "@/hooks/use-toast";
import {
  queries,
  mutations,
  useStoreQuery,
  type Tier,
} from "@/lib/store";

type FoundingTierOption = {
  tier: Tier;
  fullPrice: string;
  foundingPrice: string;
  pitch: string;
};

const FOUNDING_TIERS: FoundingTierOption[] = [
  {
    tier: "Seedling",
    fullPrice: "$47",
    foundingPrice: "$23.50",
    pitch: "Daily nourish basics, just getting started.",
  },
  {
    tier: "Rooted",
    fullPrice: "$97",
    foundingPrice: "$48.50",
    pitch: "Weekly menu plans, prayer wall, full Garden.",
  },
  {
    tier: "Flourishing",
    fullPrice: "$197",
    foundingPrice: "$98.50",
    pitch: "Coach Kiley access, partner pairing, deeper care.",
  },
];

const palettes = {
  forest: { fg: "#1E4A35", soft: "rgba(30,74,53,0.08)", ring: "var(--mnf-f)" },
  coral:  { fg: "#C0784A", soft: "rgba(192,120,74,0.10)", ring: "var(--mnf-t)" },
  gold:   { fg: "#A07820", soft: "rgba(160,120,32,0.10)", ring: "var(--mnf-gold)" },
  purple: { fg: "#5F58A8", soft: "rgba(127,119,221,0.10)", ring: "var(--mnf-purple)" },
  fiber:  { fg: "#2C6248", soft: "rgba(168,212,184,0.18)", ring: "#2C6248" },
} as const;

type Tone = keyof typeof palettes;

const toneEmoji: Record<Tone, string> = {
  forest: "🌳",
  coral: "🤝",
  gold: "🌾",
  purple: "✨",
  fiber: "🌱",
};

export default function ChallengeDetail() {
  const [, params] = useRoute<{ slug: string }>("/together/:slug");
  const slug = params?.slug ?? "";
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: joinChallenge / logChallengeDay /
  // postChallengeReflection below all WRITE with this id. `progress` reads
  // null whenever userId is null, and the `if (!progress) return null` guard
  // just below already makes those writes unreachable while unresolved; the
  // extra `if (!userId)` after it exists purely so TypeScript can see that.
  const userId = me?.id ?? null;

  const challenge = useStoreQuery(() => queries.challengeBySlug(slug));
  const progress = useStoreQuery(() =>
    challenge && userId ? queries.challengeProgress(challenge.id, userId) : null,
  );
  const reflections = useStoreQuery(() =>
    challenge ? queries.challengeReflections(challenge.id) : [],
  ) ?? [];
  const participantCount = useStoreQuery(() =>
    challenge ? queries.challengeParticipantCount(challenge.id) : 0,
  ) ?? 0;
  const foundingCount = useStoreQuery(queries.foundingMemberCount) ?? 0;
  const loggedToday = useStoreQuery(() =>
    challenge && userId ? queries.challengeLoggedToday(challenge.id, userId) : false,
  ) ?? false;

  const [pickedTier, setPickedTier] = useState<Tier>(
    (me?.tier as Tier | undefined) ?? "Rooted",
  );
  const [reflectionBody, setReflectionBody] = useState("");
  const [posted, setPosted] = useState(false);
  const { toast } = useToast();

  if (!challenge) {
    return (
      <div className="pb-24">
        <ScreenHeader
          eyebrow="Sisterhood circles"
          title={<>Circle not found</>}
          subtitle={<>This circle may have closed, or the link may be old.</>}
        />
        <div className="px-5">
          <Link
            href="/together"
            className="mnf-body inline-flex items-center gap-2 no-underline mt-4"
            style={{ color: "var(--mnf-f)", fontSize: 13, fontWeight: 600 }}
            data-testid="link-back-together"
          >
            <Icon name="arrow-left" size={14} />
            Back to all circles
          </Link>
        </div>
      </div>
    );
  }

  if (!progress || !userId) return null;

  const tone = challenge.tone as Tone;
  const palette = palettes[tone];
  const isLaunch = challenge.isLaunchChallenge;
  const isJoined = !!progress.participant;
  const isFounding = !!progress.participant?.isFoundingMember;
  const targetLabel = `${challenge.threshold.targetDays} of ${progress.windowDays} days`;
  const graceDays = challenge.threshold.graceDays;
  const startsInDays = Math.max(
    0,
    Math.ceil((challenge.startsAt - Date.now()) / (1000 * 60 * 60 * 24)),
  );
  const notYetStarted = challenge.startsAt > Date.now();

  const onJoin = () => {
    const result = mutations.joinChallenge({
      challengeId: challenge.id,
      userId,
      foundingTier: isLaunch ? pickedTier : undefined,
    });
    if (result) {
      toast({
        title: isLaunch ? "Welcome, founding member" : "Welcome, sister",
        description: isLaunch
          ? "Your half-off pricing is locked in for life."
          : "We are walking with you.",
      });
    }
  };

  const onLogToday = () => {
    if (!isJoined || loggedToday) return;
    const updated = mutations.logChallengeDay({
      challengeId: challenge.id,
      userId,
    });
    if (updated?.completedAt) {
      toast({
        title: "You crossed the line, sister",
        description: "You finished the circle. Bearing fruit.",
      });
    } else if (updated) {
      toast({
        title: "Day counted",
        description: "Grace, not pressure. Rest tonight.",
      });
    }
  };

  const onPost = () => {
    if (!reflectionBody.trim()) return;
    mutations.postChallengeReflection({
      challengeId: challenge.id,
      userId,
      body: reflectionBody,
    });
    setReflectionBody("");
    setPosted(true);
    setTimeout(() => setPosted(false), 2200);
  };

  const heroPrimary = challenge.hero?.primary || toneEmoji[tone];
  const heroSecondary = challenge.hero?.secondary;

  return (
    <div className="pb-28">
      {/* Hero band */}
      <div
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(160deg, ${palette.soft} 0%, transparent 100%)`,
          padding: "12px 22px 22px",
        }}
      >
        <Link
          href="/together"
          className="mnf-body inline-flex items-center gap-2 no-underline"
          style={{
            color: palette.fg,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
          data-testid="link-back-together"
        >
          <Icon name="arrow-left" size={14} />
          All circles
        </Link>
        <div className="flex items-start gap-3 mt-3">
          <div
            className="shrink-0 inline-flex items-center justify-center relative"
            style={{
              width: 56,
              height: 56,
              borderRadius: 18,
              background: "rgba(255,255,255,0.6)",
              fontSize: 30,
              boxShadow: `0 0 0 2px ${palette.soft}`,
            }}
            aria-hidden
          >
            <span>{heroPrimary}</span>
            {heroSecondary && (
              <span
                style={{
                  position: "absolute",
                  fontSize: 16,
                  transform: "translate(18px, -14px)",
                  opacity: 0.85,
                }}
              >
                {heroSecondary}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="mnf-eyebrow"
              style={{ color: palette.fg }}
              data-testid="text-challenge-eyebrow"
            >
              {isLaunch ? "Launch circle" : "Together circle"}
            </p>
            <h1
              className="mnf-serif leading-[1.1]"
              style={{
                fontSize: 30,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                letterSpacing: "-0.01em",
              }}
              data-testid="text-challenge-title"
            >
              {challenge.title}
            </h1>
            {challenge.subtitle && (
              <p
                className="mnf-body italic mt-1.5"
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: "var(--mnf-tg)",
                }}
              >
                {challenge.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <PillTag tone={tone === "fiber" ? "fiber" : tone === "purple" ? "purple" : tone === "coral" ? "coral" : tone === "gold" ? "gold" : "forest"}>
            {targetLabel}
          </PillTag>
          {graceDays > 0 && (
            <PillTag tone="neutral">{graceDays} grace days</PillTag>
          )}
          <PillTag tone="neutral">
            {participantCount}{" "}
            {participantCount === 1 ? "sister" : "sisters"} walking
          </PillTag>
          {notYetStarted && (
            <PillTag tone="purple">
              Begins in {startsInDays} {startsInDays === 1 ? "day" : "days"}
            </PillTag>
          )}
        </div>
      </div>

      <div className="px-5 mt-2">
        {/* Scripture anchor */}
        <GlassCard organic padding={20} className="mb-4">
          <p
            className="mnf-eyebrow mb-2"
            style={{ color: palette.fg }}
          >
            Anchored in
          </p>
          <p
            className="mnf-serif italic"
            style={{
              fontSize: 17,
              lineHeight: 1.55,
              color: "var(--mnf-fd)",
            }}
            data-testid="text-challenge-scripture"
          >
            {`"${challenge.scripture}"`}
          </p>
          <p
            className="mnf-body mt-2"
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: palette.fg,
            }}
          >
            {challenge.scriptureRef}
          </p>
        </GlassCard>

        {/* Invitation */}
        <GlassCard organic padding={20} className="mb-4">
          <p
            className="mnf-eyebrow mb-2"
            style={{ color: "var(--mnf-t)" }}
          >
            Kiley's invitation
          </p>
          <p
            className="mnf-body"
            style={{
              fontSize: 14,
              lineHeight: 1.65,
              color: "var(--mnf-tx)",
              whiteSpace: "pre-line",
            }}
            data-testid="text-challenge-invitation"
          >
            {challenge.invitation}
          </p>
        </GlassCard>

        {/* Progress + Log */}
        <GlassCard organic padding={22} className="mb-4">
          <div className="flex items-center gap-4">
            <ChallengeProgressRing
              daysLogged={progress.daysLogged}
              targetDays={progress.targetDays}
              tone={tone}
              size={108}
              strokeWidth={10}
            />
            <div className="flex-1 min-w-0">
              <p
                className="mnf-eyebrow"
                style={{ color: palette.fg }}
              >
                {isJoined
                  ? progress.completed
                    ? "Bearing fruit"
                    : "Walking with you"
                  : progress.canJoin
                    ? "Open to join"
                    : "Window closed"}
              </p>
              <h3
                className="mnf-serif"
                style={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: "var(--mnf-fd)",
                  marginTop: 2,
                  lineHeight: 1.2,
                }}
                data-testid="text-progress-headline"
              >
                {isJoined
                  ? `${progress.daysLogged} of ${progress.targetDays} days`
                  : "Join the circle"}
              </h3>
              <p
                className="mnf-body mt-1.5"
                style={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: "var(--mnf-tg)",
                }}
              >
                {progress.daysRemaining === 0
                  ? "The window has closed."
                  : `${progress.daysRemaining} ${progress.daysRemaining === 1 ? "day" : "days"} left in the window.`}
              </p>
            </div>
          </div>

          {isFounding && (
            <div className="mt-4">
              <FoundingMemberRibbon
                variant="card"
                count={foundingCount}
                endsLabel="Open while the launch circle gathers"
              />
            </div>
          )}

          {/* Log today / Join CTAs */}
          {isJoined && !progress.completed && (
            <div className="mt-5">
              <Btn
                variant="primary"
                block
                size="md"
                onClick={onLogToday}
                disabled={loggedToday}
                data-testid="button-log-today"
              >
                {loggedToday ? (
                  <>
                    <Icon name="check_circle" size={16} />
                    Logged today
                  </>
                ) : (
                  <>
                    <Icon name="eco" size={16} />
                    Log today
                  </>
                )}
              </Btn>
              <p
                className="mnf-body text-center mt-2"
                style={{
                  fontSize: 12,
                  color: "var(--mnf-tg)",
                  fontStyle: "italic",
                }}
              >
                One tap is enough. Sabbath days rest with you.
              </p>
            </div>
          )}

          {progress.completed && (
            <div
              className="mt-5 text-center"
              style={{
                padding: "14px 16px",
                borderRadius: 16,
                background: "rgba(160,120,32,0.10)",
              }}
            >
              <p
                className="mnf-serif italic"
                style={{ fontSize: 16, color: "var(--mnf-fd)" }}
              >
                "By their fruit you will recognize them."
              </p>
              <p
                className="mnf-body mt-2"
                style={{
                  fontSize: 12.5,
                  color: "var(--mnf-tg)",
                  letterSpacing: "0.04em",
                }}
              >
                You crossed the line, sister.
              </p>
            </div>
          )}
        </GlassCard>

        {/* Join card with optional tier picker */}
        {!isJoined && progress.canJoin && (
          <GlassCard organic padding={22} className="mb-4">
            <p
              className="mnf-eyebrow mb-2"
              style={{ color: "var(--mnf-t)" }}
            >
              {isLaunch ? "Founding offer" : "Step in"}
            </p>
            <h3
              className="mnf-serif"
              style={{
                fontSize: 20,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                lineHeight: 1.25,
              }}
            >
              {isLaunch
                ? "Half-off pricing, kept for life"
                : "Walk this circle with us"}
            </h3>
            {isLaunch && (
              <>
                <p
                  className="mnf-body mt-2"
                  style={{
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: "var(--mnf-tg)",
                  }}
                >
                  Founding members of the launch circle keep their tier at
                  half price for as long as they stay subscribed. Pick the
                  one that fits this season, sister.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {FOUNDING_TIERS.map((opt) => {
                    const selected = pickedTier === opt.tier;
                    return (
                      <button
                        key={opt.tier}
                        type="button"
                        onClick={() => setPickedTier(opt.tier)}
                        data-testid={`radio-tier-${opt.tier.toLowerCase()}`}
                        className="text-left transition-all"
                        style={{
                          padding: "14px 16px",
                          borderRadius: 18,
                          border: selected
                            ? "1.5px solid var(--mnf-f)"
                            : "1px solid rgba(30,74,53,0.14)",
                          background: selected
                            ? "rgba(30,74,53,0.06)"
                            : "rgba(255,255,255,0.55)",
                          cursor: "pointer",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className="inline-flex items-center justify-center shrink-0"
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              border: selected
                                ? "5px solid var(--mnf-f)"
                                : "1.5px solid rgba(30,74,53,0.35)",
                              background: "transparent",
                            }}
                            aria-hidden
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <span
                                className="mnf-serif"
                                style={{
                                  fontSize: 16,
                                  fontWeight: 500,
                                  color: "var(--mnf-fd)",
                                }}
                              >
                                {opt.tier}
                              </span>
                              <span
                                className="mnf-body"
                                style={{
                                  fontSize: 13,
                                  color: "var(--mnf-f)",
                                  fontWeight: 700,
                                }}
                              >
                                {opt.foundingPrice}
                                <span
                                  style={{
                                    color: "var(--mnf-tg)",
                                    textDecoration: "line-through",
                                    fontWeight: 500,
                                    marginLeft: 6,
                                  }}
                                >
                                  {opt.fullPrice}
                                </span>
                              </span>
                            </div>
                            <p
                              className="mnf-body mt-1"
                              style={{
                                fontSize: 12.5,
                                lineHeight: 1.45,
                                color: "var(--mnf-tg)",
                              }}
                            >
                              {opt.pitch}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {!isLaunch && (
              <p
                className="mnf-body mt-2"
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: "var(--mnf-tg)",
                }}
              >
                Reach the gentle target inside the window. There is no
                ranking, no penalty for the days that life takes.
              </p>
            )}
            <div className="mt-4">
              <Btn
                variant="primary"
                block
                size="md"
                onClick={onJoin}
                data-testid="button-join-challenge"
              >
                <Icon name="diversity_1" size={16} />
                {isLaunch ? "Join as founding member" : "Join this circle"}
              </Btn>
              {isLaunch && (
                <p
                  className="mnf-body text-center mt-2"
                  style={{
                    fontSize: 11.5,
                    color: "var(--mnf-tg)",
                    fontStyle: "italic",
                  }}
                >
                  Founding pricing is kept for life as long as your
                  subscription stays active.
                </p>
              )}
            </div>
          </GlassCard>
        )}

        {/* Reflections feed */}
        <div className="mt-2">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                Walking together
              </p>
              <h2
                className="mnf-serif"
                style={{
                  fontSize: 22,
                  fontWeight: 500,
                  color: "var(--mnf-fd)",
                }}
              >
                Reflections
              </h2>
            </div>
            <span
              className="mnf-body text-xs"
              style={{ color: "var(--mnf-tg)" }}
              data-testid="text-reflection-count"
            >
              {reflections.length}{" "}
              {reflections.length === 1 ? "voice" : "voices"}
            </span>
          </div>

          {/* Composer */}
          {isJoined && (
            <GlassCard organic padding={18} className="mb-4">
              <p
                className="mnf-eyebrow mb-2"
                style={{ color: "var(--mnf-f)" }}
              >
                Share what bloomed today
              </p>
              <textarea
                value={reflectionBody}
                onChange={(e) => setReflectionBody(e.target.value)}
                placeholder="One small thing you noticed, sister..."
                data-testid="textarea-reflection"
                className="mnf-body w-full resize-none"
                style={{
                  minHeight: 84,
                  padding: "12px 14px",
                  borderRadius: 16,
                  border: "1px solid rgba(30,74,53,0.14)",
                  background: "rgba(255,255,255,0.6)",
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: "var(--mnf-tx)",
                  outline: "none",
                }}
              />
              <div className="flex items-center justify-between mt-3">
                <span
                  className="mnf-body"
                  style={{
                    fontSize: 11.5,
                    color: "var(--mnf-tg)",
                    fontStyle: "italic",
                  }}
                >
                  {posted ? "Shared with the circle" : "Grace, not polish"}
                </span>
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={onPost}
                  disabled={!reflectionBody.trim()}
                  data-testid="button-post-reflection"
                >
                  Share reflection
                </Btn>
              </div>
            </GlassCard>
          )}

          {/* Reflections list */}
          {reflections.length === 0 ? (
            <GlassCard organic padding={20}>
              <p
                className="mnf-serif italic"
                style={{
                  fontSize: 15,
                  color: "var(--mnf-fd)",
                  lineHeight: 1.5,
                }}
              >
                The circle is quiet for now.
              </p>
              <p
                className="mnf-body mt-2"
                style={{ fontSize: 13, color: "var(--mnf-tg)" }}
              >
                When sisters share what bloomed, you will see it here.
              </p>
            </GlassCard>
          ) : (
            <div className="flex flex-col gap-3">
              {reflections.map((r) => (
                <GlassCard
                  key={r.id}
                  organic
                  padding={16}
                  data-testid={`card-reflection-${r.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar
                      initials={r.authorInitials}
                      size={38}
                      ringColor={
                        r.isCoach ? "rgba(160,120,32,0.55)" : undefined
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="mnf-serif"
                          style={{
                            fontSize: 15,
                            fontWeight: 500,
                            color: "var(--mnf-fd)",
                          }}
                        >
                          {r.authorName}
                        </span>
                        {r.isCoach && (
                          <PillTag tone="gold">Coach Kiley</PillTag>
                        )}
                        {r.dayMarker > 0 && (
                          <span
                            className="mnf-body"
                            style={{
                              fontSize: 11,
                              color: "var(--mnf-tg)",
                              fontStyle: "italic",
                            }}
                          >
                            Day {r.dayMarker}
                          </span>
                        )}
                      </div>
                      <p
                        className="mnf-body mt-1.5"
                        style={{
                          fontSize: 13.5,
                          lineHeight: 1.6,
                          color: "var(--mnf-tx)",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {r.body}
                      </p>
                      <p
                        className="mnf-body mt-2"
                        style={{
                          fontSize: 11,
                          color: "var(--mnf-tg)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {relativeTime(r.createdAt)}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}
